import { useState } from 'react'
import { Plus } from 'lucide-react'
import {
  CurrencyValue, EmptyState, ErrorState, FileUploader, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import type { UploadedFile } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { useCandidateMutations, useCandidates, useRecruitmentSources } from '../api'
import type { CandidateStatus } from '../types'
import { Select } from '@/components/ui/select'

const STATUS_TONE: Record<CandidateStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  ACTIVE: 'info',
  HIRED: 'success',
  WITHDRAWN: 'neutral',
  BLACKLISTED: 'danger',
}

/** Everybody who has applied, once, across every requisition. */
export function CandidatesPage() {
  useBreadcrumb()
  const candidates = useCandidates()
  const sources = useRecruitmentSources()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const { save } = useCandidateMutations()

  const [draft, setDraft] = useState<{
    legalEntityId: string; firstName: string; lastName: string; email: string; phone: string
    currentEmployer: string; currentTitle: string; totalExperienceYears: string
    noticePeriodDays: string; expectedSalary: string; expectedCurrency: string
    sourceId: string; cv: UploadedFile | null
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Candidates"
        subtitle="One record per person, however many jobs they apply to."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                legalEntityId: entities.data?.[0]?.id ?? '', firstName: '', lastName: '',
                email: '', phone: '', currentEmployer: '', currentTitle: '',
                totalExperienceYears: '', noticePeriodDays: '', expectedSalary: '',
                expectedCurrency: 'AED', sourceId: '', cv: null,
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New candidate
          </Button>
        }
      />

      <Card>
        {candidates.isPending && <Skeleton className="m-4 h-24" />}
        {candidates.isError && (
          <ErrorState error={candidates.error} onRetry={() => void candidates.refetch()} />
        )}
        {candidates.isSuccess && candidates.data.length === 0 && (
          <EmptyState
            title="Nobody has applied"
            description="Add a candidate here, then attach them to a requisition from its board."
          />
        )}
        {candidates.isSuccess && candidates.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Name</th>
                  <th className="p-3 text-start font-medium">Contact</th>
                  <th className="p-3 text-start font-medium">Now at</th>
                  <th className="p-3 text-end font-medium">Experience</th>
                  <th className="p-3 text-end font-medium">Expecting</th>
                  <th className="p-3 text-end font-medium">Applications</th>
                  <th className="p-3 text-start font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {candidates.data.map((candidate) => (
                  <tr key={candidate.id} className="border-b border-slate-100">
                    <td className="p-3">
                      {candidate.displayName}
                      {candidate.cvFileId && (
                        <a
                          className="ms-2 text-xs underline underline-offset-2"
                          href={`/api/v1/files/${candidate.cvFileId}/content`}
                        >
                          CV
                        </a>
                      )}
                    </td>
                    <td className="p-3 text-slate-600">
                      {candidate.email ?? candidate.phone}
                    </td>
                    <td className="p-3 text-slate-600">
                      {candidate.currentTitle ?? '—'}
                      {candidate.currentEmployer && (
                        <span className="block text-xs">{candidate.currentEmployer}</span>
                      )}
                    </td>
                    <td className="p-3 text-end text-slate-600">
                      {candidate.totalExperienceYears == null
                        ? '—' : `${candidate.totalExperienceYears} yrs`}
                    </td>
                    <td className="p-3 text-end">
                      <CurrencyValue
                        value={candidate.expectedSalary}
                        currency={candidate.expectedCurrency ?? undefined}
                      />
                    </td>
                    <td className="p-3 text-end">{candidate.applicationCount}</td>
                    <td className="p-3">
                      <StatusBadge
                        status={candidate.status}
                        tone={STATUS_TONE[candidate.status as CandidateStatus] ?? 'neutral'}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) setDraft(null) }}
        size="lg"
        title="New candidate"
        description="An email or a phone number is required — somebody nobody can reach cannot be taken through a hiring process."
        submitLabel="Save candidate"
        loading={save.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              await save.mutateAsync({
                legalEntityId: draft.legalEntityId || null,
                firstName: draft.firstName,
                lastName: draft.lastName || null,
                email: draft.email || null,
                phone: draft.phone || null,
                currentEmployer: draft.currentEmployer || null,
                currentTitle: draft.currentTitle || null,
                totalExperienceYears: draft.totalExperienceYears
                  ? Number(draft.totalExperienceYears) : null,
                noticePeriodDays: draft.noticePeriodDays
                  ? Number(draft.noticePeriodDays) : null,
                expectedSalary: draft.expectedSalary ? Number(draft.expectedSalary) : null,
                expectedCurrency: draft.expectedCurrency || null,
                sourceId: draft.sourceId || null,
                cvFileId: draft.cv?.id ?? null,
              })
              setDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That candidate could not be saved')
            }
          })()
        }}
      >
        {draft && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="cand-first">First name</Label>
                <Input
                  id="cand-first" value={draft.firstName}
                  onChange={(event) => setDraft({ ...draft, firstName: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="cand-last">Last name</Label>
                <Input
                  id="cand-last" value={draft.lastName}
                  onChange={(event) => setDraft({ ...draft, lastName: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="cand-email">Email</Label>
                <Input
                  id="cand-email" type="email" value={draft.email}
                  onChange={(event) => setDraft({ ...draft, email: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="cand-phone">Phone</Label>
                <Input
                  id="cand-phone" value={draft.phone}
                  onChange={(event) => setDraft({ ...draft, phone: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="cand-employer">Currently at</Label>
                <Input
                  id="cand-employer" value={draft.currentEmployer}
                  onChange={(event) =>
                    setDraft({ ...draft, currentEmployer: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="cand-title">Current title</Label>
                <Input
                  id="cand-title" value={draft.currentTitle}
                  onChange={(event) => setDraft({ ...draft, currentTitle: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="cand-experience">Years of experience</Label>
                <Input
                  id="cand-experience" type="number" step="0.5"
                  value={draft.totalExperienceYears}
                  onChange={(event) =>
                    setDraft({ ...draft, totalExperienceYears: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="cand-notice">Notice period, days</Label>
                <Input
                  id="cand-notice" type="number" value={draft.noticePeriodDays}
                  onChange={(event) =>
                    setDraft({ ...draft, noticePeriodDays: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="cand-expected">Expecting</Label>
                <Input
                  id="cand-expected" type="number" value={draft.expectedSalary}
                  onChange={(event) =>
                    setDraft({ ...draft, expectedSalary: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="cand-source">Where they came from</Label>
                <Select
                  id="cand-source"
                  className="w-full"
                  value={draft.sourceId}
                  onChange={(event) => setDraft({ ...draft, sourceId: event.target.value })}
                >
                  <option value="">Not recorded</option>
                  {sources.data?.filter((source) => source.active).map((source) => (
                    <option key={source.id} value={source.id}>{source.name}</option>
                  ))}
                </Select>
                {(sources.data?.length ?? 0) === 0 && (
                  <p className="mt-1 text-xs text-slate-600">
                    No source is defined. None ships with this system — the list is yours.
                  </p>
                )}
              </div>
            </div>
            <div>
              <Label>CV</Label>
              <FileUploader
                ownerType="CANDIDATE"
                files={draft.cv ? [draft.cv] : []}
                onUploaded={(file) => setDraft({ ...draft, cv: file })}
                onRemoved={() => setDraft({ ...draft, cv: null })}
              />
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
